import { cardById, isCardId, type Card, type CardColor, type CardKind } from './cards.js'
import type { CommandError } from './commands.js'
import type { Army, GameEndReason, PlayerIndex } from './state.js'
import { de } from './locales/de.js'
import { es } from './locales/es.js'
import { hi } from './locales/hi.js'

/**
 * Localization of the texts produced by the server (game log, errors).
 * The server stores structured messages; each client renders them in its
 * own language. French stays the reference text (`LogEntry.text`, `message`).
 */

export const LOCALES = ['fr', 'en', 'es', 'de', 'hi'] as const
export type Locale = (typeof LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'fr'

export function isLocale(value: unknown): value is Locale {
  return (LOCALES as readonly unknown[]).includes(value)
}

type Params = Record<string, string | number>

/**
 * Texts of the languages added after French and English (src/locales/*.ts):
 * everything the server produces, in one object per language.
 */
export interface LocaleTexts {
  cardColors: Record<CardColor, string>
  cardKinds: Record<CardKind, string>
  /** Label of a card ("Red 3", "Blue Skip", "Joker"): word order belongs to each language. */
  cardLabel: (card: Card) => string
  winReasons: Partial<Record<GameEndReason, string>>
  /** One log line; `card` names a card from its id in this language. */
  log: (m: LogMessage, name: NameOf, card: (cardId: string) => string) => string
  errors: Record<ErrorKey, string | ((p: Params) => string)>
}

const MORE: Record<Exclude<Locale, 'fr' | 'en'>, LocaleTexts> = { es, de, hi }

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export const CARD_COLOR_LABELS: Record<Locale, Record<CardColor, string>> = {
  fr: { red: 'Rouge', blue: 'Bleu', green: 'Vert', yellow: 'Jaune' },
  en: { red: 'Red', blue: 'Blue', green: 'Green', yellow: 'Yellow' },
  es: es.cardColors,
  de: de.cardColors,
  hi: hi.cardColors,
}

export const CARD_KIND_LABELS: Record<Locale, Record<CardKind, string>> = {
  fr: { number: 'Chiffre', zero: '0', skip: 'Passe ton tour', reverse: 'Inversion', plus2: '+2', wild: 'Joker', wild4: '+4' },
  en: { number: 'Number', zero: '0', skip: 'Skip', reverse: 'Reverse', plus2: '+2', wild: 'Joker', wild4: '+4' },
  es: es.cardKinds,
  de: de.cardKinds,
  hi: hi.cardKinds,
}

export function cardLabel(card: Card, locale: Locale): string {
  if (locale !== 'fr' && locale !== 'en') return MORE[locale].cardLabel(card)
  const color = card.color ? CARD_COLOR_LABELS[locale][card.color] : ''
  switch (card.kind) {
    case 'number':
      return locale === 'en' ? `${color} ${card.value}` : `${card.value} ${color}`
    case 'zero':
      return locale === 'en' ? `${color} 0` : `0 ${color}`
    case 'skip':
    case 'reverse':
    case 'plus2':
      return locale === 'en' ? `${color} ${CARD_KIND_LABELS.en[card.kind]}` : `${CARD_KIND_LABELS.fr[card.kind]} ${color}`
    case 'wild':
    case 'wild4':
      return CARD_KIND_LABELS[locale][card.kind]
  }
}

export function cardLabelFr(card: Card): string {
  return cardLabel(card, 'fr')
}

function cardName(cardId: string, locale: Locale): string {
  return isCardId(cardId) ? cardLabel(cardById(cardId), locale) : cardId
}

// ---------------------------------------------------------------------------
// Game log
// ---------------------------------------------------------------------------

export type LogMessage =
  | { id: 'game_start'; white: PlayerIndex; black: PlayerIndex }
  | { id: 'deck_reshuffled'; count: number }
  | { id: 'deck_empty'; player: PlayerIndex }
  | { id: 'card_drawn'; player: PlayerIndex; cardId: string }
  | { id: 'turn_start'; turn: number; player: PlayerIndex; army: Army }
  | { id: 'number_card'; player: PlayerIndex; moves: number }
  | { id: 'zero_card'; player: PlayerIndex }
  | { id: 'skip_card'; player: PlayerIndex; next: PlayerIndex }
  | { id: 'skip_in_check'; player: PlayerIndex }
  | { id: 'recovery_in_check'; player: PlayerIndex }
  | { id: 'no_pawn_to_recover'; cardId: string; army: Army; noSquare: boolean }
  | { id: 'pawns_recovered'; cardId: string; player: PlayerIndex; count: number }
  | { id: 'reverse_card'; white: PlayerIndex; black: PlayerIndex; player: PlayerIndex; army: Army }
  | { id: 'joker_card'; player: PlayerIndex }
  | { id: 'effect_chosen'; player: PlayerIndex; moves: number }
  | { id: 'pawn_placed'; player: PlayerIndex; square: string }
  | { id: 'no_move_turn_ends'; player: PlayerIndex }
  | { id: 'turn_end'; turn: number }
  | { id: 'draw_agreed' }
  | { id: 'draw_no_moves'; turns: number }
  | { id: 'win'; winner: PlayerIndex; reason: GameEndReason }
  | { id: 'move'; player: PlayerIndex; army: Army; san: string }
  | { id: 'series_stopped_threat' }
  | { id: 'no_move_left'; player: PlayerIndex }
  | { id: 'series_ended'; player: PlayerIndex }
  | { id: 'draw_offered'; player: PlayerIndex }
  | { id: 'draw_declined'; player: PlayerIndex }

/** Resolves a player index to the name shown in the log. */
export type NameOf = (player: PlayerIndex) => string

function s(n: number): string {
  return n > 1 ? 's' : ''
}

const WIN_REASONS: Record<'fr' | 'en', Partial<Record<GameEndReason, string>>> = {
  fr: {
    king_captured: 'capture du roi adverse',
    checkmate: 'échec et mat',
    resign: 'abandon de l’adversaire',
    abandon: 'forfait de l’adversaire (déconnexion prolongée ou inactivité)',
  },
  en: {
    king_captured: 'capturing the enemy king',
    checkmate: 'checkmate',
    resign: 'resignation',
    abandon: 'the opponent’s forfeit (prolonged disconnection or inactivity)',
  },
}

function frLog(m: LogMessage, name: NameOf): string {
  const army = (a: Army) => (a === 'w' ? 'les blancs' : 'les noirs')
  switch (m.id) {
    case 'game_start':
      return `La partie commence : ${name(m.white)} a les blancs, ${name(m.black)} a les noirs.`
    case 'deck_reshuffled':
      return `La défausse est remélangée pour former une nouvelle pioche (${m.count} cartes).`
    case 'deck_empty':
      return `${name(m.player)} ne peut pas piocher : plus aucune carte disponible.`
    case 'card_drawn':
      return `${name(m.player)} pioche et révèle ${cardName(m.cardId, 'fr')}.`
    case 'turn_start':
      return `Tour ${m.turn} : ${name(m.player)} joue ${army(m.army)}.`
    case 'number_card':
      return `${name(m.player)} joue ${m.moves} déplacement${s(m.moves)} ce tour.`
    case 'zero_card':
      return `0 : ${name(m.player)} passe son tour sans déplacement.`
    case 'skip_card':
      return `Passe ton tour : ${name(m.player)} passe son tour, ${name(m.next)} pioche et joue.`
    case 'skip_in_check':
      return `Passe ton tour : ${name(m.player)} est en échec et ne peut pas passer son tour ; il joue 1 déplacement pour parer.`
    case 'recovery_in_check':
      return `${name(m.player)} est toujours en échec : il joue 1 déplacement pour parer.`
    case 'no_pawn_to_recover':
      return `${cardName(m.cardId, 'fr')} : aucun pion à récupérer pour ${army(m.army)}${m.noSquare ? ' (aucune case libre)' : ''}.`
    case 'pawns_recovered':
      return `${cardName(m.cardId, 'fr')} : ${name(m.player)} récupère ${m.count} pion${s(m.count)} à replacer.`
    case 'reverse_card':
      return `Inversion : ${name(m.white)} contrôle désormais les blancs et ${name(m.black)} les noirs. ${name(m.player)} joue avec ${army(m.army)}.`
    case 'joker_card':
      return `Joker : ${name(m.player)} peut déplacer n’importe quelle pièce comme une dame (1 déplacement).`
    case 'effect_chosen':
      return `${name(m.player)} choisit ${m.moves} déplacement${s(m.moves)}.`
    case 'pawn_placed':
      return `${name(m.player)} replace un pion en ${m.square}.`
    case 'no_move_turn_ends':
      return `${name(m.player)} n’a aucun déplacement possible : fin du tour.`
    case 'turn_end':
      return `Fin du tour ${m.turn}.`
    case 'draw_agreed':
      return 'Partie nulle : les deux joueurs ont accepté la nulle.'
    case 'draw_no_moves':
      return `Partie nulle : ${m.turns} tours consécutifs sans déplacement de pièce.`
    case 'win':
      return `${name(m.winner)} gagne par ${WIN_REASONS.fr[m.reason] ?? m.reason}.`
    case 'move':
      return `${name(m.player)} (${army(m.army)}) joue ${m.san}.`
    case 'series_stopped_threat':
      return 'Le roi adverse est attaqué : la série de déplacements s’arrête.'
    case 'no_move_left':
      return `${name(m.player)} n’a plus aucun déplacement possible.`
    case 'series_ended':
      return `${name(m.player)} termine sa série de déplacements.`
    case 'draw_offered':
      return `${name(m.player)} propose la nulle.`
    case 'draw_declined':
      return `${name(m.player)} refuse la nulle.`
  }
}

function enLog(m: LogMessage, name: NameOf): string {
  const army = (a: Army) => (a === 'w' ? 'White' : 'Black')
  switch (m.id) {
    case 'game_start':
      return `The game begins: ${name(m.white)} has White, ${name(m.black)} has Black.`
    case 'deck_reshuffled':
      return `The discard pile is reshuffled into a new draw pile (${m.count} cards).`
    case 'deck_empty':
      return `${name(m.player)} cannot draw: no cards left.`
    case 'card_drawn':
      return `${name(m.player)} draws and reveals ${cardName(m.cardId, 'en')}.`
    case 'turn_start':
      return `Turn ${m.turn}: ${name(m.player)} plays ${army(m.army)}.`
    case 'number_card':
      return `${name(m.player)} plays ${m.moves} move${s(m.moves)} this turn.`
    case 'zero_card':
      return `0: ${name(m.player)} skips the turn without moving.`
    case 'skip_card':
      return `Skip: ${name(m.player)} loses the turn, ${name(m.next)} draws and plays.`
    case 'skip_in_check':
      return `Skip: ${name(m.player)} is in check and cannot skip the turn; they play 1 move to parry.`
    case 'recovery_in_check':
      return `${name(m.player)} is still in check: they play 1 move to parry.`
    case 'no_pawn_to_recover':
      return `${cardName(m.cardId, 'en')}: no pawn to recover for ${army(m.army)}${m.noSquare ? ' (no free square)' : ''}.`
    case 'pawns_recovered':
      return `${cardName(m.cardId, 'en')}: ${name(m.player)} recovers ${m.count} pawn${s(m.count)} to place.`
    case 'reverse_card':
      return `Reverse: ${name(m.white)} now controls White and ${name(m.black)} Black. ${name(m.player)} plays with ${army(m.army)}.`
    case 'joker_card':
      return `Joker: ${name(m.player)} may move any piece like a queen (1 move).`
    case 'effect_chosen':
      return `${name(m.player)} chooses ${m.moves} move${s(m.moves)}.`
    case 'pawn_placed':
      return `${name(m.player)} places a pawn on ${m.square}.`
    case 'no_move_turn_ends':
      return `${name(m.player)} has no legal move: the turn ends.`
    case 'turn_end':
      return `End of turn ${m.turn}.`
    case 'draw_agreed':
      return 'Draw: both players agreed to a draw.'
    case 'draw_no_moves':
      return `Draw: ${m.turns} consecutive turns without a piece moving.`
    case 'win':
      return `${name(m.winner)} wins by ${WIN_REASONS.en[m.reason] ?? m.reason}.`
    case 'move':
      return `${name(m.player)} (${army(m.army)}) plays ${m.san}.`
    case 'series_stopped_threat':
      return 'The enemy king is attacked: the series of moves stops.'
    case 'no_move_left':
      return `${name(m.player)} has no legal move left.`
    case 'series_ended':
      return `${name(m.player)} ends their series of moves.`
    case 'draw_offered':
      return `${name(m.player)} offers a draw.`
    case 'draw_declined':
      return `${name(m.player)} declines the draw.`
  }
}

export function formatLog(message: LogMessage, locale: Locale, name: NameOf): string {
  if (locale === 'fr') return frLog(message, name)
  if (locale === 'en') return enLog(message, name)
  return MORE[locale].log(message, name, (cardId) => cardName(cardId, locale))
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

const ERRORS = {
  game_over: { fr: 'La partie est terminée.', en: 'The game is over.' },
  server_only: { fr: 'Commande réservée au serveur.', en: 'This command is reserved for the server.' },
  forfeit_needs_player: {
    fr: 'Un forfait doit préciser le joueur (utiliser applyForfeit).',
    en: 'A forfeit must name the player (use applyForfeit).',
  },
  draw_already_offered: { fr: 'Proposition déjà en cours.', en: 'A draw offer is already pending.' },
  no_draw_to_accept: { fr: 'Aucune proposition de nulle à accepter.', en: 'There is no draw offer to accept.' },
  no_draw_to_decline: { fr: 'Aucune proposition de nulle à refuser.', en: 'There is no draw offer to decline.' },
  not_your_turn: { fr: 'Ce n’est pas à vous d’agir.', en: 'It is not your turn.' },
  no_effect_pending: { fr: 'Aucun effet en attente.', en: 'No effect is pending.' },
  no_effect_to_choose: { fr: 'Aucun effet à choisir.', en: 'There is no effect to choose.' },
  choose_moves: {
    fr: (p: Params) => `Choisissez entre 1 et ${p.max} déplacements.`,
    en: (p: Params) => `Choose between 1 and ${p.max} moves.`,
  },
  no_pawn_to_place: { fr: 'Aucun pion à replacer.', en: 'There is no pawn to place.' },
  invalid_pawn_square: { fr: 'Case non admissible pour replacer un pion.', en: 'A pawn cannot be placed on this square.' },
  cannot_place_pawn: { fr: 'Impossible de placer le pion ici.', en: 'The pawn cannot be placed here.' },
  not_move_phase: { fr: 'Ce n’est pas la phase de déplacement.', en: 'This is not the move phase.' },
  invalid_move: { fr: 'Déplacement non admissible.', en: 'This move is not allowed.' },
  king_attacked_square: { fr: 'Le roi ne peut pas aller sur une case attaquée.', en: 'The king cannot move to an attacked square.' },
  king_left_in_check: { fr: 'Ce coup laisserait votre roi en échec.', en: 'This move would leave your king in check.' },
  king_not_capturable: { fr: 'En mode puriste, le roi ne se prend pas.', en: 'In purist mode, the king cannot be captured.' },
  first_move_required: { fr: 'Le premier déplacement est obligatoire.', en: 'The first move is mandatory.' },
  no_series: { fr: 'Aucune série en cours.', en: 'No series of moves in progress.' },
  unknown_command: { fr: 'Commande inconnue.', en: 'Unknown command.' },
  invite_unknown: { fr: 'Code d’invitation inconnu.', en: 'Unknown invite code.' },
  game_not_found: { fr: 'Partie introuvable.', en: 'Game not found.' },
  game_full: { fr: 'Cette partie est déjà complète.', en: 'This game is already full.' },
  not_in_game: { fr: 'Vous ne participez pas à cette partie.', en: 'You are not playing in this game.' },
  game_not_started: { fr: 'La partie n’a pas encore commencé.', en: 'The game has not started yet.' },
  game_not_finished: { fr: 'La partie n’est pas terminée.', en: 'The game is not over.' },
  stale_command: {
    fr: (p: Params) => `Commande périmée : version attendue ${p.expected}, reçue ${p.received}.`,
    en: (p: Params) => `Outdated command: expected version ${p.expected}, received ${p.received}.`,
  },
  command_id_reused: { fr: 'Identifiant de commande déjà utilisé.', en: 'Command id already used.' },
  view_unavailable: { fr: 'Vue indisponible.', en: 'View unavailable.' },
  cannot_challenge_self: { fr: 'Vous ne pouvez pas vous défier vous-même.', en: 'You cannot challenge yourself.' },
  player_not_found: { fr: 'Joueur introuvable.', en: 'Player not found.' },
  rate_limited: { fr: 'Trop de requêtes, réessayez dans un instant.', en: 'Too many requests, try again in a moment.' },
  invalid_request: { fr: 'Requête invalide.', en: 'Invalid request.' },
  internal: { fr: 'Erreur interne du serveur.', en: 'Internal server error.' },
  server_unreachable: { fr: 'Le serveur ne répond pas.', en: 'The server is not responding.' },
  username_taken: { fr: 'Ce pseudo est déjà pris.', en: 'This username is already taken.' },
  bad_credentials: { fr: 'Pseudo ou mot de passe incorrect.', en: 'Wrong username or password.' },
  too_many_attempts: { fr: 'Trop de tentatives, réessayez dans une minute.', en: 'Too many attempts, try again in a minute.' },
  invalid_data: { fr: 'Données invalides.', en: 'Invalid data.' },
  username_format: {
    fr: 'Pseudo : 3 à 20 lettres, chiffres, « _ », « - » ou « . ».',
    en: 'Username: 3 to 20 letters, digits, “_”, “-” or “.”.',
  },
  password_length: { fr: 'Mot de passe : 6 caractères minimum.', en: 'Password: at least 6 characters.' },
  credentials_required: { fr: 'Pseudo et mot de passe requis.', en: 'Username and password are required.' },
  invalid_session: { fr: 'Session invalide.', en: 'Invalid session.' },
  match_not_ranked: { fr: 'Partie non classée.', en: 'Unranked game.' },
  login_failed: { fr: 'Connexion impossible.', en: 'Could not sign in.' },
  feedback_empty: { fr: 'Écrivez un message ou donnez une note.', en: 'Write a message or give a score.' },
  feedback_rate_limited: {
    fr: 'Merci ! Vous avez déjà envoyé plusieurs messages : réessayez un peu plus tard.',
    en: 'Thank you! You have already sent several messages: please try again a little later.',
  },
  tournament_unknown: { fr: 'Code de tournoi inconnu.', en: 'Unknown tournament code.' },
  tournament_started: { fr: 'Ce tournoi a déjà commencé.', en: 'This tournament has already started.' },
  tournament_full: { fr: 'Ce tournoi est complet.', en: 'This tournament is full.' },
  tournament_not_host: { fr: 'Seul l’organisateur peut faire cela.', en: 'Only the organizer can do this.' },
  tournament_not_open: { fr: 'Les inscriptions de ce tournoi sont closes.', en: 'Registration for this tournament is closed.' },
  tournament_needs_players: { fr: 'Il faut au moins 2 joueurs pour lancer le tournoi.', en: 'At least 2 players are needed to start the tournament.' },
  tournament_over: { fr: 'Ce tournoi est terminé.', en: 'This tournament is over.' },
  tournament_too_many: {
    fr: 'Vous organisez déjà trop de tournois en cours : terminez-en un ou annulez-en un.',
    en: 'You are already organizing too many tournaments: finish or cancel one first.',
  },
  tournament_no_rematch: {
    fr: 'Pas de revanche dans un tournoi : la suite se joue depuis le tableau.',
    en: 'There is no rematch in a tournament: continue from the bracket.',
  },
} satisfies Record<string, { fr: string | ((p: Params) => string); en: string | ((p: Params) => string) }>

export type ErrorKey = keyof typeof ERRORS

export function formatError(key: ErrorKey, locale: Locale, params: Params = {}): string {
  const text = locale === 'fr' || locale === 'en' ? ERRORS[key][locale] : MORE[locale].errors[key]
  return typeof text === 'function' ? text(params) : text
}

export function isErrorKey(value: unknown): value is ErrorKey {
  return typeof value === 'string' && Object.hasOwn(ERRORS, value)
}

/** Builds a command error: the key for the clients, the French text as a fallback. */
export function commandError(code: CommandError['code'], key: ErrorKey, params?: Params): CommandError {
  return { code, message: formatError(key, 'fr', params), key, ...(params ? { params } : {}) }
}

/** Text of an error in the client's language (older servers only sent `message`). */
export function errorText(error: { message: string; key?: string; params?: Params }, locale: Locale): string {
  return isErrorKey(error.key) ? formatError(error.key, locale, error.params) : error.message
}
